import { Link } from '@tanstack/react-router';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { type CSSProperties, Fragment, useState } from 'react';

import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { AgencyContextModal } from '@apps/web/components/phoebe_chat_view/artifacts/record_preview/agency_context_modal';
import { MemoryRecordModal } from '@apps/web/components/phoebe_chat_view/artifacts/record_preview/memory_record_modal';
import { useMemoryRecordMarkdown } from '@apps/web/components/phoebe_chat_view/artifacts/record_preview/memory_record_preview';
import { PlaybookRecordModal } from '@apps/web/components/phoebe_chat_view/artifacts/record_preview/playbook_record_modal';
import { RecordPreview } from '@apps/web/components/phoebe_chat_view/artifacts/record_preview/record_preview';
import { useSavedNoteOutcomes } from '@apps/web/components/phoebe_chat_view/artifacts/saved_note_outcomes_context';
import { useEntityDetailClick } from '@apps/web/components/phoebe_chat_view/assistant_responses/entity_detail_click_context';
import { ReferenceBadge } from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import { SaveNoteWritingIcon } from '@apps/web/components/phoebe_chat_view/tool_calls/record_settings_tool_ui';
import { type SaveNoteArtifact } from '@apps/web/components/phoebe_chat_view/transcript_types';
import {
    type NoteOutcome,
    type NoteOutcomeEntity,
} from '@apps/web/lib/agent_note_outcome_copy';
import { cn } from '@apps/web/lib/utils';

const STAGGER_STEP_MS = 450;

/**
 * The person the change landed on, using the same badge Phoebe's own messages
 * use — same colour, same hover card — and opening the same in-panel detail.
 * `forceInPanelDetail` keeps it from navigating: checking what Phoebe recorded
 * should not cost the conversation you are in.
 */
function EntityBadge({ entity }: { readonly entity: NoteOutcomeEntity }) {
    return (
        <ReferenceBadge
            forceInPanelDetail={true}
            reference={{
                type: entity.type,
                id: entity.id,
                display_name: entity.name,
            }}
        />
    );
}

/**
 * One saved note, collapsed to a single row.
 *
 * At rest the row carries only what the conversation does not already say:
 * what changed, and who it was about. Phoebe's reply just above has already
 * described the note in her own words, so repeating its text spends a line to
 * say nothing new. The text moves inside the disclosure, one click away for
 * anyone checking exactly what was filed.
 */

const OPEN_LINK_CLASS =
    'text-muted-foreground hover:text-foreground flex shrink-0 cursor-pointer items-center gap-1 text-[11px] leading-4';

// Where an org-wide change surfaces in the product. Domains keyed to a person
// open that person instead, and a domain with no product surface of its own is
// left out rather than pointed at an approximate page.
const ORG_WIDE_ROUTES = {
    playbook_rule: '/playbooks',
    outreach_filter: '/settings/skip-allow-lists',
    recommendation_default: '/settings/recommendations',
} as const;

/**
 * Opens the record the note wrote, mirroring the destination action on the
 * admin notes page.
 *
 * A change tied to a person opens their detail in place, so checking what
 * Phoebe recorded does not cost the conversation you are in. An org-wide
 * change has no in-panel surface and navigates to the settings page that owns
 * it.
 */
export function OpenRecordLink({
    entity,
    domain,
    resultRefId,
}: {
    readonly entity: NoteOutcomeEntity | null;
    readonly domain?: NoteOutcome['domain'];
    readonly resultRefId?: string | null;
}) {
    const { onCaregiverClick, onClientClick } = useEntityDetailClick();
    const { organizationId } = usePhoebeChatOrganizationScope();
    const [isRecordOpen, setIsRecordOpen] = useState(false);
    if (domain === 'agency_context' && organizationId) {
        return (
            <Fragment>
                <button
                    type="button"
                    onClick={() => {
                        setIsRecordOpen(true);
                    }}
                    className={OPEN_LINK_CLASS}
                >
                    Open
                    <ArrowUpRight aria-hidden={true} className="h-3 w-3" />
                </button>
                {isRecordOpen ? (
                    <AgencyContextModal
                        organizationId={organizationId}
                        fallbackText=""
                        onClose={() => {
                            setIsRecordOpen(false);
                        }}
                    />
                ) : null}
            </Fragment>
        );
    }
    // Memory's record is the person's compiled document, not their profile
    // page, so it opens the document rather than the entity detail every other
    // person-scoped domain uses.
    if (domain === 'memory' && entity && organizationId) {
        return (
            <Fragment>
                <button
                    type="button"
                    onClick={() => {
                        setIsRecordOpen(true);
                    }}
                    className={OPEN_LINK_CLASS}
                >
                    Open
                    <ArrowUpRight aria-hidden={true} className="h-3 w-3" />
                </button>
                {isRecordOpen ? (
                    <MemoryRecordModal
                        organizationId={organizationId}
                        entity={entity}
                        onClose={() => {
                            setIsRecordOpen(false);
                        }}
                    />
                ) : null}
            </Fragment>
        );
    }
    if (!entity) {
        // A playbook rule has a record to show, so it opens in place like
        // every other row. The remaining org-wide domains are settings pages
        // with no single record, and those do navigate.
        if (domain === 'playbook_rule' && resultRefId && organizationId) {
            return (
                <Fragment>
                    <button
                        type="button"
                        onClick={() => {
                            setIsRecordOpen(true);
                        }}
                        className={OPEN_LINK_CLASS}
                    >
                        Open
                        <ArrowUpRight aria-hidden={true} className="h-3 w-3" />
                    </button>
                    {isRecordOpen ? (
                        <PlaybookRecordModal
                            organizationId={organizationId}
                            playbookId={resultRefId}
                            onClose={() => {
                                setIsRecordOpen(false);
                            }}
                        />
                    ) : null}
                </Fragment>
            );
        }
        const to =
            domain && domain in ORG_WIDE_ROUTES
                ? ORG_WIDE_ROUTES[domain as keyof typeof ORG_WIDE_ROUTES]
                : null;
        if (!to) {
            return null;
        }
        return (
            <Link to={to} className={OPEN_LINK_CLASS}>
                Open
                <ArrowUpRight aria-hidden={true} className="h-3 w-3" />
            </Link>
        );
    }
    const open = entity.type === 'caregiver' ? onCaregiverClick : onClientClick;
    if (!open) {
        return null;
    }
    return (
        <button
            type="button"
            onClick={() => {
                open(entity.id);
            }}
            className={OPEN_LINK_CLASS}
        >
            Open
            <ArrowUpRight aria-hidden={true} className="h-3 w-3" />
        </button>
    );
}

export function SaveNoteArtifactCard({
    artifact,
    staggerIndex = 0,
    defaultOpen = true,
    showWhileUnresolved = false,
}: {
    readonly artifact: SaveNoteArtifact;
    readonly staggerIndex?: number;
    readonly defaultOpen?: boolean;
    // The run debrief writes its own notes and has no pipeline result to wait
    // for, so it opts in to rendering unresolved. Everywhere else a row that
    // cannot yet say what changed says nothing at all.
    readonly showWhileUnresolved?: boolean;
}) {
    const allOutcomes = useSavedNoteOutcomes(artifact.noteId);
    const { organizationId } = usePhoebeChatOrganizationScope();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    // Staged proposals render in the approval dock above the composer, not
    // here — the transcript records what was said, the dock holds what is
    // waiting. This card keeps only the outcomes with nothing to approve.
    const outcomes = allOutcomes.filter((outcome) => !outcome.isProposal);
    // The outcome that still needs the scheduler heads the row — a note the
    // classifier split across destinations can have its ask at any index,
    // and item order must not decide which line leads. The remaining
    // outcomes list inside.
    const headline =
        outcomes.find((outcome) => outcome.tone === 'needs-you') ?? outcomes[0];
    const extra = outcomes.filter((outcome) => outcome !== headline);
    const memoryMarkdown = useMemoryRecordMarkdown(
        organizationId,
        headline?.domain === 'memory'
            ? (headline.entity ?? undefined)
            : undefined,
    );

    // Nothing to report yet: still routing, or it landed nowhere. A row
    // reading "Noted" tells the scheduler less than the reply above it
    // already did, so it waits until it has something to say.
    if (headline === undefined && !showWhileUnresolved) {
        return null;
    }
    // The chat carries asks; the Changes tab carries state. An applied change
    // is something Phoebe already described in her reply, so repeating it here
    // costs a row to say nothing new. A draft or proposal is different: it is
    // addressed to the scheduler, it is blocking, and Phoebe cannot resolve
    // it herself. headline is the first needs-you outcome when one exists,
    // so this hides the card only when NO outcome needs the scheduler.
    if (headline !== undefined && headline.tone !== 'needs-you') {
        return null;
    }
    // Memory is the one store written in two stages: the fragment lands with
    // the note, the document is recompiled after. Holding the whole row until
    // the document exists beats showing a header with nothing under it.
    if (headline?.domain === 'memory' && memoryMarkdown === null) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="save-note-chip-in border-border/60 border-l-training-accent/50 bg-muted/20 ml-3 w-[calc(100%-0.75rem)] max-w-xl min-w-0 overflow-hidden rounded-r-lg border border-l-2"
            style={
                {
                    '--save-note-stagger-delay': `${
                        staggerIndex * STAGGER_STEP_MS
                    }ms`,
                } as CSSProperties
            }
        >
            {/* Siblings, not nested: the badge and the open link are their
                own controls, and a button inside the disclosure button is
                invalid and unreachable by keyboard. */}
            <div className="border-border/50 flex min-w-0 items-center gap-1.5 border-b px-2.5 py-1.5">
                <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => {
                        setIsOpen((open) => !open);
                    }}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left"
                >
                    <SaveNoteWritingIcon className="text-muted-foreground h-3 w-3 shrink-0 opacity-75" />
                    <span
                        className={cn(
                            'min-w-0 truncate text-[11px] leading-4',
                            // The one state that is not finished: recorded, but
                            // inert until somebody approves it.
                            headline?.tone === 'needs-you'
                                ? 'text-foreground/80 font-medium'
                                : 'text-muted-foreground',
                        )}
                    >
                        {headline?.text ?? 'Noted'}
                    </span>
                </button>
                {headline && (
                    <OpenRecordLink
                        entity={headline.entity}
                        domain={headline.domain}
                        resultRefId={headline.resultRefId}
                    />
                )}
                <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Hide details' : 'Show details'}
                    onClick={() => {
                        setIsOpen((open) => !open);
                    }}
                    className="shrink-0 cursor-pointer"
                >
                    <ChevronDown
                        aria-hidden={true}
                        className={cn(
                            'text-muted-foreground h-3 w-3 opacity-55 transition-transform motion-reduce:transition-none',
                            isOpen && 'rotate-180',
                        )}
                    />
                </button>
            </div>
            {isOpen && (
                <div className="flex flex-col items-start gap-1.5 px-2.5 py-2">
                    <RecordPreview text={artifact.title ?? artifact.content} />
                    {extra.map((outcome) => (
                        <span
                            key={outcome.itemId}
                            className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-1 text-[11px] leading-4"
                        >
                            {outcome.text}
                            {outcome.entity && (
                                <EntityBadge entity={outcome.entity} />
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
